import { ref, type Ref } from 'vue'
import type {
  TableColumn,
  TableData,
  PivotConfig,
  OfflineChange,
  Version,
  Comment,
  CollaborationEdit,
  AuditLog,
  TableEvents,
  TableDialogs,
  CommentContext
} from '../types'
import type { DataTableFilterMeta } from 'primevue/datatable'

interface DataTableProps {
  columns: TableColumn[]
  value: TableData[]
}

type EmitFn = {
  (event: 'selection-change', selection: TableData[]): void
  (event: 'column-resize', data: { element: HTMLElement; delta: number }): void
  (event: 'filter-change', filters: DataTableFilterMeta): void
  (event: 'sort', data: { field: string; order: number; multisortmeta?: { field: string; order: number }[] }): void
  (event: 'page', data: { first: number; rows: number; page: number; pageCount: number }): void
  (event: 'row-click', data: { originalEvent: Event; data: TableData; index: number }): void
  (event: 'row-dblclick', data: { originalEvent: Event; data: TableData; index: number }): void
  (event: 'row-expand', data: { data: TableData }): void
  (event: 'row-collapse', data: { data: TableData }): void
  (event: 'column-reorder', data: { dragIndex: number; dropIndex: number }): void
  (event: 'cell-edit', data: { data: TableData; field: string; value: unknown }): void
}

export interface TableStateRef {
  selectedRows: TableData[]
  filters: DataTableFilterMeta
  first: number
  sortField: string
  sortOrder: number
  pageSize: number
  loading: boolean
  expandedRows: TableData[]
  visibleColumns: TableColumn[]
  globalFilterFields: string[]
  multiSortMode: boolean
  pivotMode: boolean
  pivotConfig?: PivotConfig
  offlineMode: boolean
  pendingChanges: OfflineChange[]
  currentVersion?: Version
  activeComments: Comment[]
  activeEditors: CollaborationEdit[]
  auditLogs: AuditLog[]
  events: TableEvents
  dialogs: TableDialogs
  currentCommentContext?: CommentContext
}

interface TableReturn {
  state: TableStateRef
  editing: {
    editValue: Ref<unknown>
    currentRow: Ref<number | null>
    currentField: Ref<string | null>
    startEditing: (rowIndex: number, field: string, value?: unknown) => void
    finishEditing: () => void
    cancelEditing: () => void
    isEditing: (rowIndex: number, field: string) => boolean
    isFocused: (rowIndex: number, field: string) => boolean
    hasValidationError: (rowIndex: number, field: string) => boolean
    getValidationError: (rowIndex: number, field: string) => string | null
    handleKeyNavigation: (event: KeyboardEvent, rowIndex: number, colIndex: number, rowCount: number, colCount: number) => void
  }
  refreshData: () => void
  exportData: () => void
  importData: () => void
  events: TableEvents
  dialogs: TableDialogs
}

export const useDataTable = (props: DataTableProps, emit: EmitFn): TableReturn => {
  const events: TableEvents = {
    handlers: {
      onSelectionChange: (selection: TableData[]) => {
        emit('selection-change', selection)
      },
      onColumnResize: (event: { element: HTMLElement; delta: number }) => {
        emit('column-resize', event)
      },
      onFilterChange: (filters: DataTableFilterMeta) => {
        emit('filter-change', filters)
      },
      onSort: (event: { field: string; order: number; multisortmeta?: { field: string; order: number }[] }) => {
        emit('sort', event)
      },
      onPage: (event: { first: number; rows: number; page: number; pageCount: number }) => {
        emit('page', event)
      },
      onRowClick: (event: { originalEvent: Event; data: TableData; index: number }) => {
        emit('row-click', event)
      },
      onRowDblClick: (event: { originalEvent: Event; data: TableData; index: number }) => {
        emit('row-dblclick', event)
      },
      onRowExpand: (event: { data: TableData }) => {
        emit('row-expand', event)
      },
      onRowCollapse: (event: { data: TableData }) => {
        emit('row-collapse', event)
      },
      onColumnReorder: (event: { dragIndex: number; dropIndex: number }) => {
        emit('column-reorder', event)
      },
      onCellEdit: (event: { data: TableData; field: string; value: unknown }) => {
        emit('cell-edit', event)
      },
      onVirtualScroll: (event: { first: number; rows: number }) => {
        state.value.first = event.first
      },
      onRowContextMenu: (event: { originalEvent: Event; data: TableData }) => {
        // Prevent default context menu
        event.originalEvent.preventDefault()
      },
      onColumnDragEnd: (event: { column: TableColumn; newIndex: number }) => {
        const columns = [...state.value.visibleColumns]
        const column = columns.find(col => col.field === event.column.field)
        if (column) {
          const index = columns.indexOf(column)
          columns.splice(index, 1)
          columns.splice(event.newIndex, 0, column)
          state.value.visibleColumns = columns
        }
      },
      onRowDragEnd: (event: { data: TableData; newIndex: number }) => {
        const rows = [...props.value]
        const row = rows.find(r => r.id === event.data.id)
        if (row) {
          const index = rows.indexOf(row)
          rows.splice(index, 1)
          rows.splice(event.newIndex, 0, row)
          // Emit update to parent
          emit('selection-change', rows)
        }
      }
    }
  }

  const dialogs: TableDialogs = {
    exportFormat: { value: '' },
    exportFormats: [
      { label: 'CSV', value: 'csv' },
      { label: 'Excel', value: 'excel' },
      { label: 'PDF', value: 'pdf' },
      { label: 'JSON', value: 'json' }
    ],
    commentsSidebar: false,
    versionHistoryDialog: false,
    importDialog: false,
    visualizationDialog: false,
    pivotDialog: false,
    openImportDialog: () => {
      state.value.dialogs.importDialog = true
    },
    openVisualizationDialog: () => {
      state.value.dialogs.visualizationDialog = true
    },
    openPivotDialog: () => {
      state.value.dialogs.pivotDialog = true
    },
    openAdvancedFilter: () => {
      // Will be implemented when filter UI is ready
    },
    openColumnCustomizer: () => {
      // Will be implemented when column customizer UI is ready
    },
    openSaveViewDialog: () => {
      // Will be implemented when save view UI is ready
    },
    openBatchEditDialog: () => {
      // Will be implemented when batch edit UI is ready
    },
    openVersionHistory: () => {
      state.value.dialogs.versionHistoryDialog = true
    },
    openComments: (rowId: string | number, field: string) => {
      state.value.currentCommentContext = { rowId, field }
      state.value.dialogs.commentsSidebar = true
    },
    openSyncStatus: () => {
      // Will be implemented when sync status UI is ready
    },
    openAuditLogs: () => {
      // Will be implemented when audit logs UI is ready
    },
    confirmBatchDelete: () => {
      // Will be implemented when batch delete confirmation UI is ready
    }
  }

  const state = ref<TableStateRef>({
    selectedRows: [],
    filters: {} as DataTableFilterMeta,
    first: 0,
    sortField: '',
    sortOrder: 1,
    pageSize: 10,
    loading: false,
    expandedRows: [],
    visibleColumns: props.columns.filter(col => !col.hidden),
    globalFilterFields: props.columns.map(col => col.field),
    multiSortMode: true,
    pivotMode: false,
    pivotConfig: undefined,
    offlineMode: false,
    pendingChanges: [],
    currentVersion: undefined,
    activeComments: [],
    activeEditors: [],
    auditLogs: [],
    currentCommentContext: undefined,
    events,
    dialogs
  })

  const editing = {
    editValue: ref<unknown>(null),
    currentRow: ref<number | null>(null),
    currentField: ref<string | null>(null),

    startEditing: (rowIndex: number, field: string, value?: unknown) => {
      editing.editValue.value = value ?? null
      editing.currentRow.value = rowIndex
      editing.currentField.value = field
    },

    finishEditing: () => {
      editing.editValue.value = null
      editing.currentRow.value = null
      editing.currentField.value = null
    },

    cancelEditing: () => {
      editing.editValue.value = null
      editing.currentRow.value = null
      editing.currentField.value = null
    },

    isEditing: (rowIndex: number, field: string) => {
      return editing.currentRow.value === rowIndex && editing.currentField.value === field
    },

    isFocused: (rowIndex: number, field: string) => {
      return editing.currentRow.value === rowIndex && editing.currentField.value === field
    },

    hasValidationError: (rowIndex: number, field: string) => {
      return editing.currentRow.value === rowIndex && editing.currentField.value === field && editing.editValue.value === null
    },

    getValidationError: (rowIndex: number, field: string) => {
      if (editing.hasValidationError(rowIndex, field)) {
        return 'Value is required'
      }
      return null
    },

    handleKeyNavigation: (
      event: KeyboardEvent,
      rowIndex: number,
      colIndex: number,
      rowCount: number,
      colCount: number
    ) => {
      switch (event.key) {
        case 'ArrowUp':
          if (rowIndex > 0) {
            editing.startEditing(rowIndex - 1, state.value.visibleColumns[colIndex].field)
          }
          break
        case 'ArrowDown':
          if (rowIndex < rowCount - 1) {
            editing.startEditing(rowIndex + 1, state.value.visibleColumns[colIndex].field)
          }
          break
        case 'ArrowLeft':
          if (colIndex > 0) {
            editing.startEditing(rowIndex, state.value.visibleColumns[colIndex - 1].field)
          }
          break
        case 'ArrowRight':
          if (colIndex < colCount - 1) {
            editing.startEditing(rowIndex, state.value.visibleColumns[colIndex + 1].field)
          }
          break
      }
    }
  }

  const refreshData = () => {
    state.value.loading = true
    // Implement refresh logic
    state.value.loading = false
  }

  const exportData = () => {
    // Will be implemented when export functionality is ready
  }

  const importData = () => {
    // Will be implemented when import functionality is ready
  }

  return {
    state: state.value,
    editing,
    refreshData,
    exportData,
    importData,
    events,
    dialogs
  }
}
